export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const AVATAR_MAX_BYTES = 10 * 1024 * 1024; // matches backend multer limit
export const AVATAR_OUTPUT_SIZE = 512;

/** Returns an error message, or null when the file is an acceptable avatar source. */
export function validateAvatarFile(file: File): string | null {
  if (!file.type.startsWith('image/')) return 'Please select an image file';
  if (file.size > AVATAR_MAX_BYTES) return 'File size must be less than 10MB';
  return null;
}

/** Bounding-box size of a w×h rectangle rotated by `rotation` degrees. */
export function rotateSize(width: number, height: number, rotation: number): { width: number; height: number } {
  const rad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
}

function createImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error('Failed to load image')));
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

/**
 * Draws the rotated source image, extracts the crop region reported by
 * react-easy-crop (`croppedAreaPixels`), and exports a square JPEG.
 */
export async function getCroppedBlob(
  imageSrc: string,
  crop: PixelCrop,
  rotation = 0,
  outputSize = AVATAR_OUTPUT_SIZE,
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const { width: stageW, height: stageH } = rotateSize(image.width, image.height, rotation);

  const stage = document.createElement('canvas');
  stage.width = Math.round(stageW);
  stage.height = Math.round(stageH);
  const stageCtx = stage.getContext('2d');
  if (!stageCtx) throw new Error('Canvas 2D context unavailable');
  stageCtx.translate(stage.width / 2, stage.height / 2);
  stageCtx.rotate((rotation * Math.PI) / 180);
  stageCtx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement('canvas');
  out.width = outputSize;
  out.height = outputSize;
  const outCtx = out.getContext('2d');
  if (!outCtx) throw new Error('Canvas 2D context unavailable');
  outCtx.drawImage(stage, crop.x, crop.y, crop.width, crop.height, 0, 0, outputSize, outputSize);
  stage.width = 0; // release backing store

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to export image'))),
      'image/jpeg',
      0.85,
    );
  });
}
